import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@heroui/react';
import { create } from 'zustand';
import { api } from '@/lib/api';
import { humanizeApiError } from '@/lib/humanizeApiError';
import { Button } from '@/components/Button';
import { joinVoice } from '@/lib/voiceCommands';
import { BROADCAST_CONSENT_VERSION } from '@/lib/broadcastConsent';

/**
 * The on-air notice for a tournament voice room.
 *
 * Shown at the door of the room, once, the first time someone tries to enter —
 * which is the only moment the question is answerable. The alternative that was
 * considered and rejected was a switch in the profile: it asks months before it
 * matters, in a screen nobody opens, and what it grants has no edges. Here the
 * person is one click from speaking, they can see whose room it is, and what
 * they agree to stops at this room.
 *
 * Declining simply closes it. The room exists to go out live; there is no
 * half-measure to offer, and pretending otherwise — a silent seat, a
 * "listen-only" mode — would be a worse answer than saying so plainly.
 */

interface BroadcastConsentState {
  /** Channel awaiting an answer, or null when nothing is being asked. */
  channelId: string | null;
  /** Asked while already sitting in the room (it became a tournament room):
   *  accepting joins nothing — least of all after the person has left. */
  inRoom: boolean;
  /** Bumped on every answer, so the in-room banner re-reads the consent. */
  answered: number;
  ask: (channelId: string, opts?: { inRoom?: boolean }) => void;
  close: () => void;
}

export const useBroadcastConsent = create<BroadcastConsentState>((set) => ({
  channelId: null,
  inRoom: false,
  answered: 0,
  ask: (channelId, opts) => set({ channelId, inRoom: opts?.inRoom === true }),
  close: () => set((s) => ({ channelId: null, inRoom: false, answered: s.answered + 1 })),
}));

/**
 * Raise the question from outside React — the join refusal arrives in a socket
 * ack, not in a component.
 */
export function askBroadcastConsent(channelId: string, opts?: { inRoom?: boolean }): void {
  useBroadcastConsent.getState().ask(channelId, opts);
}

export function BroadcastConsentModal() {
  const { t } = useTranslation();
  const channelId = useBroadcastConsent((s) => s.channelId);
  const inRoom = useBroadcastConsent((s) => s.inRoom);
  const close = useBroadcastConsent((s) => s.close);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each time the question is asked (again) it starts clean: a refusal from
  // the last attempt must not greet the next one.
  useEffect(() => {
    if (channelId !== null) setError(null);
  }, [channelId]);

  async function accept() {
    if (!channelId || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.acceptBroadcastConsent(channelId, BROADCAST_CONSENT_VERSION);
      close();
      // The join that was refused is retried here rather than by the caller:
      // the person pressed "connect" once and expects to land in the room, not
      // to press it again after answering a question they did not raise.
      // Asked while already inside (the room became a tournament room under
      // them), there is nothing to join — and if they left meanwhile, joining
      // would put them back somewhere they chose to leave.
      if (!inRoom) joinVoice(channelId);
    } catch (err) {
      const h = humanizeApiError(err);
      setError(h.hint ? `${h.title}. ${h.hint}` : h.title);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal.Backdrop
      isOpen={channelId !== null}
      onOpenChange={(v) => {
        if (!v && !busy) close();
      }}
    >
      <Modal.Container size="sm">
        <Modal.Dialog>
          <Modal.Header>
            <Modal.Heading>{t('broadcastConsent.title')}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-3 text-sm text-ink">
              <p>{t('broadcastConsent.body')}</p>
              <p className="text-ink-muted">{t('broadcastConsent.scope')}</p>
              {error && <p className="text-danger">{error}</p>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={close} disabled={busy}>
                {t('broadcastConsent.decline')}
              </Button>
              <Button onClick={accept} disabled={busy}>
                {t('broadcastConsent.accept')}
              </Button>
            </div>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
